import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { trackFormSubmission, trackLeadGenerated } from '../lib/analytics';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  fullName: '',
  mobile: '',
  email: '',
  propertyAddress: '',
  suburb: '',
  state: '',
  postcode: ''
};

const isValidAuMobile = (value) => /^04\d{8}$/.test(value);

const ContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [mobileError, setMobileError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Typing manually clears any previously auto-filled suburb/postcode
  const handleAddressChange = (text) => {
    setFormData(prev => ({ ...prev, propertyAddress: text, suburb: '', state: '', postcode: '' }));
  };

  const handleAddressSelect = ({ formattedAddress, suburb, state, postcode }) => {
    setFormData(prev => ({ ...prev, propertyAddress: formattedAddress, suburb, state, postcode }));
  };

  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, mobile: digits }));
    if (mobileError && isValidAuMobile(digits)) setMobileError('');
  };

  const handleMobileBlur = () => {
    if (formData.mobile && !isValidAuMobile(formData.mobile)) {
      setMobileError('Mobile must start with 04 and be 10 digits (e.g. 0412345678)');
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!isValidAuMobile(formData.mobile)) {
      setMobileError('Mobile must start with 04 and be 10 digits (e.g. 0412345678)');
      toast.error('Invalid mobile number', {
        description: 'Please enter an Australian mobile starting with 04 (10 digits).',
      });
      return;
    }

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not loaded. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);

    // Track form submission attempt
    trackFormSubmission('contact_form');

    try {
      const token = await executeRecaptcha('contact_form_submit');

      await axios.post(
        `${BACKEND_URL}/api/submit-contact`,
        { ...formData, recaptchaToken: token },
        { headers: { 'Content-Type': 'application/json' } }
      );

      // Track successful lead generation (GA4 conversion)
      trackLeadGenerated('contact_form');

      setFormData(EMPTY_FORM);
      setMobileError('');
      navigate('/thank-you', { state: { submitted: true, type: 'contact' } });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMessage = Array.isArray(detail)
        ? (detail[0]?.msg || '').replace(/^Value error, /, '') || 'Please check your details and try again.'
        : detail || "Please try again or contact us directly.";
      toast.error('Submission Failed', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [executeRecaptcha, formData, navigate]);

  return (
    <section id="contact" className="section-container contact-section">
      <div className="section-content">
        <div className="contact-header">
          <h2 className="section-title">Find Me an Agent</h2>
          <p className="section-description">
            Enter your details and we'll connect you with trusted local agents. Takes under a minute.
          </p>
        </div>

        <div className="contact-wrapper">
          <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
            <div className="form-group">
              <label htmlFor="fullName" className="form-label">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="form-input"
                autoComplete="name"
                placeholder="Your full name"
                data-testid="contact-name-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobile" className="form-label">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleMobileChange}
                onBlur={handleMobileBlur}
                className={`form-input${mobileError ? ' form-input-error' : ''}`}
                autoComplete="tel"
                inputMode="numeric"
                pattern="04[0-9]{8}"
                maxLength={10}
                placeholder="e.g. 0412345678"
                title="Australian mobile starting with 04, 10 digits"
                aria-invalid={!!mobileError}
                aria-describedby={mobileError ? 'mobile-error' : undefined}
                data-testid="contact-mobile-input"
                required
              />
              {mobileError && (
                <span id="mobile-error" className="form-error" role="alert" data-testid="contact-mobile-error">
                  {mobileError}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                autoComplete="email"
                placeholder="you@example.com"
                data-testid="contact-email-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="propertyAddress" className="form-label">
                Property Address <span className="required">*</span>
              </label>
              <AddressAutocomplete
                value={formData.propertyAddress}
                onChange={handleAddressChange}
                onSelect={handleAddressSelect}
                inputProps={{
                  id: 'propertyAddress',
                  name: 'propertyAddress',
                  className: 'form-input',
                  placeholder: 'Start typing your address',
                  required: true,
                  'data-testid': 'contact-address-input',
                }}
              />
              {formData.suburb && (
                <span className="form-hint address-confirmed" data-testid="contact-address-confirmed">
                  {formData.suburb}{formData.state ? ` ${formData.state}` : ''}{formData.postcode ? ` ${formData.postcode}` : ''} confirmed
                </span>
              )}
            </div>

            <button
              type="submit"
              className="form-submit-btn"
              data-testid="contact-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Find Me a Local Agent'}
            </button>
          </form>

          <div className="privacy-notice">
            <h3 className="privacy-title">Privacy & Confidence</h3>
            <p className="privacy-text">
              Your information is kept strictly confidential and used only to connect you with suitable real estate agents in your area. No spam. No pressure.{' '}
              <Link to="/privacy-policy" className="privacy-link" data-testid="contact-privacy-link">Read our Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
